import { AspectRatio, Box, Center, Container, Flex, Heading, Image, Text } from '@chakra-ui/react';
import { useResource } from 'datx-jsonapi-react';
import { FC, useEffect } from 'react';

import { useLibLynx } from '@/hooks/useLibLynx';
import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryLiblynxAuthorizationGrants } from '@/queries/liblynx-authorization-grants';
import { getOriginalSeries } from '@/queries/originals-series';
import { ILibLynxMetadata } from '@/types/LibLynx';

export const OriginalsHeader: FC = () => {
  const { id: seriesId } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });
  const { data: liblynxData } = useResource(queryLiblynxAuthorizationGrants('auth_grant'));
  const { data } = useResource(getOriginalSeries(seriesId));
  const libLynx = useLibLynx();

  useEffect(() => {
    if (!liblynxData || !data) return;
    const libLynxMetadata: ILibLynxMetadata = {
      title: data.title,
      dtype: 'article',
      doi: seriesId,
    };

    libLynx.send('trackItemInvestigation', { tm: libLynxMetadata });
  }, [liblynxData]);

  return (
    <Center bgColor="black" mt="-16" position="relative">
      <AspectRatio objectFit="cover" w="full" minH="500px" maxW="2400px" h="full" ratio={16 / 9}>
        {/* on the image itself, not the Center behind it - that black is the
            letterbox either side of the 2400px cap on ultrawide screens */}
        <Image
          src={data?.originalHeaderImageUrl}
          srcSet={`
              ${data?.largeHeaderImageUrl} 1200w
              ${data?.mediumHeaderImageUrl} 600w
              ${data?.smallHeaderImageUrl} 300w
            `}
          objectFit="cover"
          objectPosition="top"
          bg="gray.50"
          alt="Originals header"
        />
      </AspectRatio>
      <Container py="10" variant="light" h="100%" p="4" position="absolute" top="0">
        <Flex h="100%" flexDirection="column" justifyContent="flex-end">
          <Box w={{ base: '100%', md: '50%' }} color="white">
            <Image
              maxW={{ base: '72px', lg: '100%' }}
              src="/images/originals/originals-logo.svg"
              alt="Originals"
              alignSelf="baseline"
            />
            <Heading mt={2} fontSize={{ base: '4xl', lg: '7xl' }}>
              {`${data?.author?.firstName} ${data?.author?.lastName}`}
            </Heading>
            <Text lineHeight={1} fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}>
              {data?.title}
            </Text>
            <Box w="1rem" borderBottom="2px solid white" my={{ base: 4, xl: 8 }} />
            <Text fontFamily="serif" fontStyle="italic" fontSize={{ base: 'sm', xl: '2xl' }}>
              {data?.description}
            </Text>
          </Box>
        </Flex>
      </Container>
    </Center>
  );
};
